import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Apartment, AutoAwesome, DeleteSweep, Hub, WarningAmber } from '@mui/icons-material';
import { ConfirmationDialog } from '../dialog/ConfirmationDialog';
import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { deleteMediaInBucket } from '../../utils/server/storageUtils';
import { useAuth } from '../../../context/AuthContext';
import { getActiveChatbotId, getActiveTenantId } from '../../utils/server/authUtils';
import { useAppTranslation } from '../../hooks/useAppTranslation';

export function AdminPageHeader() {
    const { t, tr } = useAppTranslation();
    const { token } = useAuth();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

    const activeTenantId = getActiveTenantId({ token }) || null;
    const activeChatbotId = getActiveChatbotId({ token, tenantId: activeTenantId }) || null;
    const canDeleteMedia = Boolean(token && activeChatbotId);

    function handleDeleteMediaPressed() {
        if (!canDeleteMedia) {
            showToastError(t.adminSelectActiveChatbotFirst);
            return;
        }

        setIsDeleteDialogOpen(true);
    }

    async function deleteAllMedia() {
        if (!token) {
            showToastError(t.pleaseLoginAgain);
            setIsDeleteDialogOpen(false);
            return;
        }

        try {
            const scopedTenantId = getActiveTenantId({ token }) || null;
            const scopedChatbotId = getActiveChatbotId({ token, tenantId: scopedTenantId }) || null;

            if (!scopedChatbotId) {
                throw new Error(t.adminDeleteDialogNoBot);
            }

            const result = await deleteMediaInBucket(token, [], scopedChatbotId, scopedTenantId);
            const deletedCount = Number(result?.deleted?.[0]?.filesDeleted || 0);
            showToastSuccess(
                deletedCount > 0
                    ? tr('adminDeleteSuccessCount', { count: deletedCount })
                    : t.adminDeleteSuccessNone
            );
        } catch (err) {
            const message = err?.message || String(err);
            showToastError(tr('adminDeleteFailed', { message }));
        } finally {
            setIsDeleteDialogOpen(false);
        }
    }

    const dialogContent = activeChatbotId
        ? `${t.adminDeleteDialogScopedIntro}

${t.adminTenantLabel}: ${activeTenantId || '—'}
${t.adminChatbotLabel}: ${activeChatbotId}`
        : t.adminDeleteDialogNoBot;

    return (
        <Box
            sx={{
                width: '100%',
                p: { xs: 2.2, md: 3 },
                borderRadius: '28px',
                border: '1px solid var(--admin-hero-border)',
                background: 'var(--admin-hero-bg)',
                boxShadow: 'var(--elevated-shadow)',
                backdropFilter: 'blur(20px)',
            }}
        >
            <ConfirmationDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onAccept={deleteAllMedia}
                headerText={t.adminDeleteDialogHeader}
                content={dialogContent}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box>
                    <Chip
                        icon={<AutoAwesome />}
                        label={t.adminBadgePremium}
                        sx={{
                            mb: 1.25,
                            color: 'var(--admin-hero-chip-text)',
                            border: '1px solid var(--admin-hero-chip-border)',
                            backgroundColor: 'var(--admin-hero-chip-bg)',
                            '& .MuiChip-icon': { color: 'var(--accent-color)' },
                        }}
                    />
                    <Typography
                        variant='h3'
                        sx={{
                            fontSize: { xs: '1.7rem', md: '2.2rem' },
                            fontWeight: 800,
                            color: 'var(--admin-hero-title)',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {t.adminHeaderTitle}
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.75,
                            maxWidth: 760,
                            color: 'var(--admin-hero-description)',
                            lineHeight: 1.65,
                        }}
                    >
                        {t.adminHeaderDescription}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.4, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<Apartment />}
                            label={activeTenantId ? `${t.adminTenantLabel}: ${activeTenantId}` : t.adminHeaderTenantNotSelected}
                            sx={{
                                color: 'var(--admin-hero-chip-text)',
                                border: '1px solid var(--admin-hero-chip-border)',
                                backgroundColor: 'var(--admin-hero-chip-bg)',
                            }}
                        />
                        <Chip
                            icon={<Hub />}
                            label={activeChatbotId ? `${t.adminChatbotLabel}: ${activeChatbotId}` : t.adminHeaderChatbotNotSelected}
                            sx={{
                                color: 'var(--admin-hero-chip-text)',
                                border: '1px solid var(--admin-hero-chip-border)',
                                backgroundColor: 'var(--admin-hero-chip-bg)',
                            }}
                        />
                    </Stack>

                    <Stack direction='row' spacing={0.75} alignItems='center' sx={{ mt: 1.15 }}>
                        <WarningAmber sx={{ color: 'var(--admin-hero-warning)', fontSize: 18 }} />
                        <Typography sx={{ color: 'var(--admin-hero-muted)', fontSize: '0.84rem' }}>
                            {t.adminHeaderScopeWarning}
                        </Typography>
                    </Stack>
                </Box>

                <Button
                    onClick={handleDeleteMediaPressed}
                    endIcon={<DeleteSweep />}
                    variant='contained'
                    disabled={!canDeleteMedia}
                    sx={{
                        borderRadius: '16px',
                        px: 2.25,
                        py: 1.15,
                        minWidth: { xs: '100%', md: 'auto' },
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                        boxShadow: '0 16px 30px rgba(220, 38, 38, 0.22)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                        },
                        '&.Mui-disabled': {
                            color: 'var(--admin-hero-muted)',
                            background: 'var(--button-disabled-bg)',
                            boxShadow: 'none',
                        },
                    }}
                >
                    {canDeleteMedia ? t.deleteMedia : t.adminDeleteButtonSelectChatbot}
                </Button>
            </Box>
        </Box>
    );
}
